/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import get from "lodash/get";
import { StoryCard } from "../basic/story-card.js";
import { Slider } from "./slider";
import { wrapCollectionLayout } from "@quintype/components";
import "./two-col-carousel.module.css";
import { CollectionName } from "../atoms/collection-name/index.js";
import { getNumberOfStoriesToShow, getScrollSpeed, isEager, getRowMetadata } from "../../utils";
import PropTypes from "prop-types";

function CarouselTemplate({ story, eager = false }) {
  return (
    <StoryCard
      eager={eager}
      story={story}
      config={{ image: true, imageAspectRatio: [16, 9], imageWidth: [400, 800] }}
    />
  );
}
CarouselTemplate.propTypes = {
  story: PropTypes.object,
  eager: PropTypes.bool
};

function TwoColCarouselBase({
  stories = [],
  associatedMetadata = {},
  metadata = {},
  collection = {},
  config = {},
  index
}) {
  const configClone = Object.assign({}, config, { image: true, imageAspectRatio: [16, 9], imageWidth: [400] });
  const configCloneForBgImg = Object.assign({}, config, { image: true, imageAspectRatio: [4, 3], imageWidth: [400] });
  const cardStoryCount = 2;
  const originalStoryCount = stories.length;
  if (originalStoryCount < 3) {
    return (
      <div data-comment="At least three stories required for a TwoColCarousel">
        At least three stories required for a TwoColCarousel
      </div>
    );
  }

  let carouselStoryCount = getNumberOfStoriesToShow(metadata.numberOfStoriesInCarousel);
  if (originalStoryCount < carouselStoryCount + cardStoryCount) {
    carouselStoryCount = originalStoryCount - cardStoryCount;
  }
  const pbAheadConfig = getRowMetadata(config, associatedMetadata);
  const { show_collection_title: collectionName = true } = pbAheadConfig;

  return (
    <div styleName="wrapper two-col-carousel" data-test-id="two-col-carousel-component">
      <div styleName="collection-name-wrapper">
        {collectionName ? <CollectionName name={collection.name} config={config} section={collection} /> : null}
      </div>
      <div styleName="container">
        <div styleName="carousel">
          <Slider
            stories={stories.slice(0, carouselStoryCount)}
            limit={carouselStoryCount}
            infinite={carouselStoryCount > 1}
            itemTemplate={CarouselTemplate}
            dot={""}
            config={config}
            collection={collection}
            arrow={carouselStoryCount > 1}
            autoplay={getScrollSpeed(pbAheadConfig)}
            associatedMetadata={pbAheadConfig}
            rtl={get(config, ["theme-attributes", "language_direction"]) === "right"}
            numberOfStoriesToShow={carouselStoryCount}
            eager={isEager(index, true)}
          />
        </div>

        <div styleName={`story-with-card fix-height`}>
          <StoryCard
            config={configClone}
            story={stories[carouselStoryCount]}
            hideAuthor={true}
            sectionInlineStyle={{ border: "none" }}
            eager={isEager(index, true)}
          />
        </div>

        <div styleName={`story-on-image fix-height`}>
          <StoryCard
            type="imageBackground"
            story={stories[carouselStoryCount + 1]}
            hideAuthor={true}
            config={configCloneForBgImg}
            sectionInlineStyle={{ border: "none" }}
            eager={isEager(index)}
          />
        </div>
      </div>
    </div>
  );
}
TwoColCarouselBase.propTypes = {
  stories: PropTypes.array,
  associatedMetadata: PropTypes.object,
  metadata: PropTypes.object,
  collection: PropTypes.object,
  config: PropTypes.object,
  index: PropTypes.number
};
export const TwoColCarousel = wrapCollectionLayout(TwoColCarouselBase);

TwoColCarousel.storyLimit = 12;
